<template>
  <div class="flex bg-gray-50" style="height:calc(100vh - 64px);">
    <!-- Sidebar (collapsible) -->
    <aside
      v-show="sidebarOpen"
      class="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full overflow-y-auto">
      <Sidebar
        :pages="pages"
        :selected-page-id="selectedPageId"
        :business="business"
        :brand-palette="brandPalette"
        :color-tokens="template.color_tokens"
        :blog-enabled-initial="blogEnabled"
        :blog-posts-path="blogPostsPath"
        :chat-initial="chatSettings"
        :bar-initial="barSettings"
        :popup-initial="popupSettings"
        :seo-settings-initial="seoSettings"
        :header-initial="headerSettings"
        :footer-initial="footerSettings"
        :seo-defaults="seoDefaults"
        :google-maps-key="googleMapsKey"
        :saving="brandSaving"
        :cloneable-template-pages="cloneableTemplatePages"
        :entities="entities"
        @select-page="selectPage($event)"
        @save-business="saveBusiness"
        @save-brand="saveBrand"
        @save-blog="saveBlog"
        @save-chat="saveChat"
        @save-bar="saveBar"
        @save-popup="savePopup"
        @save-header="saveHeader"
        @save-footer="saveFooter"
        @save-seo-settings="saveSeoSettings"
        @add-page="onAddPage"
        @delete-page="onDeletePage" />
    </aside>

    <!-- Editor column (expands to fill when the preview is hidden) -->
    <main :class="['p-6 overflow-y-auto h-full', previewOpen ? 'w-[480px] flex-shrink-0' : 'flex-1']">
      <div class="mb-6 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 min-w-0">
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs flex-shrink-0"
            :title="sidebarOpen ? 'Hide pages panel' : 'Show pages panel'"
            :aria-label="sidebarOpen ? 'Hide pages panel' : 'Show pages panel'"
            :aria-pressed="sidebarOpen"
            @click="sidebarOpen = !sidebarOpen">
            <EditorIcon name="sidebar" class="w-4 h-4" />
          </button>
          <div class="min-w-0">
            <h1 class="heading-4 text-gray-900 truncate">
              {{ selectedPage?.title || 'Pick a page' }}
            </h1>
            <p class="text-caption text-gray-500 truncate">
              {{ template.name }} · v{{ template.version }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <!-- Publish state: amber dot when there are edits since last publish. -->
          <span
            class="badge badge-warning"
            :title="hasUnpublishedChanges ? 'You have changes that aren\'t live yet — hit Publish to deploy them' : 'Your live site is up to date'">
            <span
              :class="['w-1.5 h-1.5 rounded-full', hasUnpublishedChanges ? 'bg-amber-500' : 'bg-green-500']"
              aria-hidden="true" />
            {{ publishStateLabel }}
          </span>
          <!-- Undo / redo -->
          <div class="flex items-center">
            <button
              type="button"
              class="btn btn-ghost btn-icon btn-xs"
              title="Undo (⌘Z)"
              aria-label="Undo"
              :disabled="!canUndo"
              @click="doUndo">
              <EditorIcon name="undo" class="w-4 h-4" />
            </button>
            <button
              type="button"
              class="btn btn-ghost btn-icon btn-xs"
              title="Redo (⌘⇧Z)"
              aria-label="Redo"
              :disabled="!canRedo"
              @click="doRedo">
              <EditorIcon name="redo" class="w-4 h-4" />
            </button>
          </div>
          <!-- Persistent save-state chip (errors are sticky + dismissible). -->
          <span
            v-if="saveChip"
            :class="saveChip.class"
            role="status"
            aria-live="polite">
            {{ saveChip.label }}
            <button
              v-if="saveState === 'error'"
              type="button"
              class="ml-1 font-bold leading-none"
              aria-label="Dismiss error"
              @click="saveState = 'idle'">×</button>
          </span>
          <button
            v-if="selectedPage"
            type="button"
            :disabled="generatingImages"
            :class="['btn btn-ghost btn-icon btn-xs flex-shrink-0',
                     generatingImages ? 'text-blue-600' : 'text-gray-600']"
            :title="generatingImages ? 'Generating images…' : 'Generate AI images for this page'"
            :aria-label="generatingImages ? 'Generating images' : 'Generate AI images for this page'"
            @click="onGeneratePageImages">
            <EditorIcon name="sparkles" :class="['w-4 h-4', { 'animate-pulse': generatingImages }]" />
          </button>
          <button
            v-if="selectedPage"
            type="button"
            :class="['btn btn-ghost btn-icon btn-xs flex-shrink-0',
                     seoOpen ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100']"
            :aria-pressed="seoOpen"
            title="Edit this page's SEO (search + social)"
            aria-label="Edit page SEO"
            @click="toggleSeo">
            <EditorIcon name="search" class="w-4 h-4" />
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs flex-shrink-0"
            :title="previewOpen ? 'Hide preview' : 'Show preview'"
            :aria-label="previewOpen ? 'Hide preview' : 'Show preview'"
            :aria-pressed="previewOpen"
            @click="previewOpen = !previewOpen">
            <EditorIcon name="preview" class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- SEO editor: opened from the masthead "SEO" button. Per-page search +
           social settings with live previews. Takes over the editor column. -->
      <SeoEditor
        v-if="seoOpen && selectedPage"
        :key="`seo-${selectedPage.id}`"
        :page="selectedPage"
        :seo-defaults="seoDefaults"
        :upload-image="api.uploadImage"
        @close="closeSeo"
        @save="onSaveSeo" />

      <!-- Form editor: opened by clicking a form in the live preview. Takes
           over the editor column until closed. -->
      <FormEditor
        v-else-if="selectedForm"
        :key="selectedForm.id"
        :form="selectedForm"
        @close="closeForm"
        @save-fields="onSaveFormFields"
        @save-settings="onSaveFormSettings" />

      <div v-else-if="!selectedPage" class="empty-state">
        <div class="empty-state-icon">
          <EditorIcon name="sidebar" class="w-6 h-6" />
        </div>
        <p class="empty-state-description">
          Select a page from the sidebar to start editing.
        </p>
      </div>

      <div v-else ref="sectionList" class="space-y-4">
        <div
          v-if="showCoachmark"
          class="alert alert-info">
          <div class="alert-content">
            <div class="alert-description">
              <strong>Tip:</strong> click any text or image in the preview to jump straight to it — or edit it in the cards below.
            </div>
          </div>
          <button
            type="button"
            class="alert-close"
            aria-label="Dismiss tip"
            @click="dismissCoachmark">
            <EditorIcon name="close" class="w-4 h-4" />
          </button>
        </div>
        <!-- Empty page: every section was removed. -->
        <div
          v-if="selectedPage.blocks.length === 0"
          class="empty-state">
          <div class="empty-state-icon">
            <EditorIcon name="sidebar" class="w-6 h-6" />
          </div>
          <h3 class="empty-state-title">
            This page has no sections
          </h3>
          <p class="empty-state-description">
            {{ (selectedPage.addable_sections || []).length ? 'Add one below to start building this page.' : 'There are no sections available to add to this page.' }}
          </p>
        </div>

        <SectionCard
          v-for="(block, idx) in selectedPage.blocks"
          :key="block.id"
          :ref="(el) => setCardRef(block.id, el)"
          :data-block-id="block.id"
          :block="block"
          :is-first="idx === 0"
          :is-last="idx === selectedPage.blocks.length - 1"
          :brand-swatches="brandSwatchList"
          :page-links="pageLinks"
          @update="(content, patch) => onSectionUpdate(block.id, content, patch)"
          @toggle="onToggleVisibility(block.id, $event)"
          @move="onMoveSection(block.id, $event)"
          @add-item="onAddItem(block.id)"
          @remove-item="onRemoveItem(block.id, $event)"
          @reorder-items="onReorderItems(block.id, $event)"
          @update-overrides="onUpdateOverrides(block.id, $event)"
          @preview="livePatch($event)"
          @field-focus="highlightInPreview($event)"
          @generate-image="onGenerateImage(block.id, $event)"
          @duplicate="onDuplicateSection(block.id)"
          @delete="onDeleteSection(block.id)"
          @open-history="openHistory(block.id)" />

        <!-- Add a new section from the template's catalog. -->
        <div v-if="(selectedPage.addable_sections || []).length" class="pt-1">
          <button
            v-if="!addSectionOpen"
            type="button"
            class="btn btn-secondary btn-sm btn-full"
            @click="addSectionOpen = true">
            <EditorIcon name="plus" class="w-4 h-4" /> Add section
          </button>
          <div v-else class="border border-gray-200 rounded-lg p-2 bg-surface-elevated">
            <div class="card-header flex items-center justify-between">
              <span class="text-xs font-medium text-gray-700">Add a section</span>
              <button type="button" class="btn btn-ghost btn-icon btn-xs" aria-label="Cancel" @click="addSectionOpen = false">
                <EditorIcon name="close" class="w-4 h-4" />
              </button>
            </div>
            <button
              v-for="s in selectedPage.addable_sections"
              :key="s.site_template_section_id"
              type="button"
              class="w-full text-left px-3 py-2 text-sm text-gray-800 rounded hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              @click="onAddSection(s.site_template_section_id)">
              {{ s.name }}
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Live preview iframe (collapsible) -->
    <PreviewPane
      v-show="previewOpen"
      ref="previewPane"
      :website-id="websiteId"
      :slug="selectedPage?.slug"
      :reload-key="previewReloadKey" />

    <!-- Version history drawer -->
    <div
      v-if="historyBlockId"
      class="fixed inset-0 z-40 flex"
      role="dialog"
      aria-modal="true"
      aria-labelledby="version-history-title">
      <div class="flex-1 bg-black bg-opacity-30" @click="closeHistory" />
      <aside class="w-96 bg-surface-elevated border-l border-gray-200 shadow-xl flex flex-col">
        <header class="card-header flex items-start justify-between">
          <div class="min-w-0">
            <h2 id="version-history-title" class="text-sm font-semibold text-gray-900">
              Edit history
            </h2>
            <p class="text-[11px] text-gray-500 truncate">
              {{ historyBlockName }}
            </p>
          </div>
          <button
            ref="historyCloseBtn"
            type="button"
            class="btn btn-ghost btn-icon btn-xs"
            aria-label="Close edit history"
            @click="closeHistory">
            <EditorIcon name="close" class="w-4 h-4" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto">
          <!-- Loading -->
          <div v-if="historyLoading" class="flex items-center gap-2 p-4">
            <div class="spinner spinner-sm" />
            <p class="text-caption">
              Loading history…
            </p>
          </div>
          <!-- Error -->
          <div v-else-if="historyError" class="m-4">
            <div class="alert alert-error">
              <div class="alert-content">
                <div class="alert-title">
                  Couldn’t load history
                </div>
                <div class="alert-description">
                  {{ historyError }}
                </div>
              </div>
            </div>
            <button type="button" class="btn btn-secondary btn-sm mt-2" @click="openHistory(historyBlockId)">
              Try again
            </button>
          </div>
          <!-- Empty state -->
          <div v-else-if="historyVersions.length === 0" class="empty-state">
            <div class="empty-state-icon">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 1.5M21 12a9 9 0 1 1-3-6.7L21 8m0-4v4h-4" />
              </svg>
            </div>
            <h3 class="empty-state-title">
              No edits yet
            </h3>
            <p class="empty-state-description">
              As you edit <span class="font-medium">{{ historyBlockName }}</span>, each saved change is captured here — so you can compare and roll back to any earlier version.
            </p>
          </div>
          <ol v-else class="divide-y divide-gray-100">
            <li v-for="ver in historyVersions" :key="ver.id" class="p-3 hover:bg-gray-50">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-xs font-medium text-gray-900">
                    v{{ ver.version }} · {{ ver.change_summary }}
                  </p>
                  <p class="text-[11px] text-gray-500">
                    {{ ver.editor_name }} · {{ formatDate(ver.created_at) }}
                  </p>
                </div>
                <button
                  v-if="ver.restorable"
                  type="button"
                  class="btn btn-secondary btn-sm"
                  @click="restoreVersion(ver.id)">
                  Restore
                </button>
                <span v-else class="text-[11px] text-gray-400">Current</span>
              </div>
            </li>
          </ol>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, provide, reactive, ref, watch } from 'vue'
import Sortable from 'sortablejs'
import Sidebar from './Sidebar.vue'
import SectionCard from './SectionCard.vue'
import FormEditor from './FormEditor.vue'
import SeoEditor from './SeoEditor.vue'
import PreviewPane from './PreviewPane.vue'
import EditorIcon from './EditorIcon.vue'
import { makeApi } from './api.js'
import { confirmAlert } from '~/javascript/utils/confirm_alert'
import consumer from '../../channels/consumer'

const props = defineProps({
  initialPayload: { type: Object, required: true },
  csrfToken: { type: String, required: true },
  websiteId: { type: String, required: true }
})

const template = ref(props.initialPayload.template || {})
const pages = reactive(props.initialPayload.pages || [])
const business = reactive({ ...(props.initialPayload.website?.business || {}) })
const brandPalette = reactive({ ...(props.initialPayload.website?.brand_palette || {}) })
const blogEnabled = ref(props.initialPayload.website?.blog_enabled ?? false)
const chatSettings = reactive({ ...(props.initialPayload.website?.chat || {}) })
const barSettings = reactive({ ...(props.initialPayload.website?.bar || {}) })
const popupSettings = reactive({ ...(props.initialPayload.website?.popup || {}) })
const seoSettings = reactive({ ...(props.initialPayload.website?.seo_settings || {}) })
const footerSettings = reactive({ content: {}, social_platforms: [], sources: {}, ...(props.initialPayload.website?.footer || {}) })
const headerSettings = reactive({ content: {}, sources: {}, ...(props.initialPayload.website?.header || {}) })
const googleMapsKey = props.initialPayload.google_maps_key || ''

// Publish state: whether edits exist since the last publish + when that was.
// Flips to dirty on the first successful save this session; resets on reload
// after a publish (the payload recomputes it server-side).
const hasUnpublishedChanges = ref(!!props.initialPayload.website?.has_unpublished_changes)
const lastPublishedAt = ref(props.initialPayload.website?.last_published_at || null)
function markUnpublished() { hasUnpublishedChanges.value = true }
const publishStateLabel = computed(() => {
  if (hasUnpublishedChanges.value) return 'Unpublished changes'
  if (lastPublishedAt.value) return `Published ${relativeTime(lastPublishedAt.value)}`
  return 'Not published yet'
})
function relativeTime(iso) {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}
const blogPostsPath = computed(() => `/dashboard/website/${props.websiteId}/blog_posts`)
// Page fan-out: list of cloneable template_pages + the entity catalog
// (services + service_areas) for the "Add page" picker.
const cloneableTemplatePages = ref(props.initialPayload.cloneable_template_pages || [])
const entities = reactive(props.initialPayload.entities || { services: [], service_areas: []})

const selectedPageId = ref(pages[0]?.id || null)
const selectedPage = computed(() => pages.find((p) => p.id === selectedPageId.value) || null)

// Customer forms (id, slug, name, fields, settings, webhooks). Clicking a
// form in the live preview opens FormEditor for that form in place of the
// section list.
const forms = reactive(props.initialPayload.forms || [])
const selectedFormId = ref(null)
const selectedForm = computed(() => forms.find((f) => f.id === selectedFormId.value) || null)
function openForm(formId) {
  if (forms.some((f) => f.id === formId)) selectedFormId.value = formId
}
function closeForm() { selectedFormId.value = null }

// Per-page SEO panel — opened from the masthead "SEO" button, takes over the
// editor column (like FormEditor) until closed.
const seoOpen = ref(false)
const seoDefaults = reactive(props.initialPayload.website?.seo_defaults || {})
function toggleSeo() {
  if (seoOpen.value) { seoOpen.value = false; return }
  flushPendingEdits()
  closeForm()
  seoOpen.value = true
}
function closeSeo() { seoOpen.value = false }

const api = makeApi({ csrfToken: props.csrfToken, endpoints: props.initialPayload.endpoints })
// Make api available to deeply-nested components (e.g. ImageField's upload).
provide('templateEditorApi', api)

const brandSaving = ref(false)
const generatingImages = ref(false)
const previewPane = ref(null)

// Collapsible panels. On narrow screens the 3 columns collide, so we default
// the sidebar + preview closed below ~xl and let the user toggle them.
// Responsive defaults: the three columns (sidebar + editor + preview) need
// ~1280px to coexist. On laptops/tablets we open fewer panels by default so
// the editor isn't crushed; the toggle buttons still let the user open them.
const viewportWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 1440)
const sidebarOpen = ref(viewportWidth() >= 1024)
const previewOpen = ref(viewportWidth() >= 1280)

// Persistent save-state for the header chip — the single status surface.
// 'idle' | 'saving' | 'saved' | 'error'. Errors stay until dismissed; success
// notices auto-clear. Replaces the old 1.8s-only toast.
const saveState = ref('idle')
const saveMessage = ref('')
let savedResetTimer = null
const saveChip = computed(() => {
  if (saveState.value === 'idle') return null
  const cls = {
    saving: 'badge badge-default',
    saved: 'badge badge-success',
    error: 'badge badge-error'
  }[saveState.value]
  const fallback = { saving: 'Saving…', saved: 'All changes saved', error: 'Save failed' }[saveState.value]
  return { class: cls, label: saveMessage.value || fallback }
})
function markSaving() { saveState.value = 'saving'; saveMessage.value = '' }
function markSaved(msg) {
  saveState.value = 'saved'
  saveMessage.value = msg || ''
  markUnpublished()
  clearTimeout(savedResetTimer)
  savedResetTimer = setTimeout(() => { if (saveState.value === 'saved') saveState.value = 'idle' }, 2000)
}
function markError(msg) { saveState.value = 'error'; saveMessage.value = msg || '' }

// Bumped after a server-side save so PreviewPane reloads its iframe. Debounced
// + scroll-preserving (PreviewPane restores scroll) so rapid edits coalesce
// into one smooth reconcile instead of a reload-per-keystroke flicker. Content
// edits also live-patch the preview instantly via patchField, so this trailing
// reload is rarely even perceptible.
const previewReloadKey = ref(0)
let previewTimer = null
function bumpPreview() {
  clearTimeout(previewTimer)
  previewTimer = setTimeout(() => { previewReloadKey.value += 1 }, 400)
}

// Instantly reflect a content edit in the preview (no reload).
function livePatch(payload) {
  if (payload) previewPane.value?.patchField?.(payload)
}

// Frame the matching element in the preview when a field gains focus, so the
// editor and preview stay visually linked (the overlay handles the highlight).
function highlightInPreview(payload) {
  if (payload?.section && payload?.field) previewPane.value?.highlightField?.(payload)
}

const addSectionOpen = ref(false)

// Duplicate a section: server deep-copies the block + shifts positions; we
// insert the returned block right after the original in the rail (array order
// drives the rail; the rebuilt preview reflects the real positions).
async function onDuplicateSection(blockId) {
  const page = selectedPage.value
  if (!page) return
  try {
    markSaving()
    const data = await api.duplicateSection(blockId)
    const idx = page.blocks.findIndex((b) => b.id === blockId)
    if (data.block && idx >= 0) page.blocks.splice(idx + 1, 0, { ...data.block, content: data.block.content || {} })
    resetHistory() // structural change — see resetHistory()
    bumpPreview()
    markSaved('Section duplicated')
  } catch (e) {
    markError(`Couldn't duplicate: ${e.message}`)
  }
}

// Delete a section — confirmed (structural + not on the content undo stack).
async function onDeleteSection(blockId) {
  const page = selectedPage.value
  if (!page) return
  const block = page.blocks.find((b) => b.id === blockId)
  const name = block?.section?.name || 'this section'
  const ok = await confirmAlert({
    title: `Remove ${name}?`,
    text: 'This removes the section from this page. You can add it back from “Add section”.',
    icon: 'warning',
    confirmButtonText: 'Remove section',
    variant: 'danger'
  })
  if (!ok) return
  try {
    markSaving()
    await api.deleteSection(blockId)
    const idx = page.blocks.findIndex((b) => b.id === blockId)
    if (idx >= 0) page.blocks.splice(idx, 1)
    resetHistory() // structural change — see resetHistory()
    bumpPreview()
    markSaved('Section removed')
  } catch (e) {
    markError(`Couldn't remove: ${e.message}`)
  }
}

// Add a fresh section instance from the catalog (default content), appended.
async function onAddSection(sectionId) {
  const page = selectedPage.value
  if (!page) return
  addSectionOpen.value = false
  try {
    markSaving()
    const data = await api.addSection(page.id, sectionId)
    if (data.block) page.blocks.push({ ...data.block, content: data.block.content || {} })
    resetHistory() // structural change — see resetHistory()
    bumpPreview()
    markSaved('Section added')
  } catch (e) {
    markError(`Couldn't add section: ${e.message}`)
  }
}

// One-time "click the preview to edit" tip; dismissal persists per browser.
const COACHMARK_KEY = 'mly-editor-clicktoedit-tip'
const showCoachmark = ref(safeLocalGet(COACHMARK_KEY) !== 'dismissed')
function dismissCoachmark() {
  showCoachmark.value = false
  try { window.localStorage.setItem(COACHMARK_KEY, 'dismissed') } catch (e) { /* ignore */ }
}
function safeLocalGet(key) {
  try { return window.localStorage.getItem(key) } catch (e) { return null }
}

// Site pages offered in link-field pickers (label + relative href matching
// what BuildSite writes: index.html / <slug>.html).
const pageLinks = computed(() => pages.map((p) => {
  const slug = (p.slug || '').replace(/^\//, '')
  return { label: p.title || slug || 'Home', href: !slug || slug === 'index' ? 'index.html' : `${slug}.html` }
}))

// Brand colors offered as quick swatches in the style popover — the customer's
// brand_palette overrides first, then the template's color-token defaults
// (where the real palette lives until a customer overrides it).
const brandSwatchList = computed(() => {
  const fromPalette = Object.values(brandPalette || {})
  const fromTokens = Object.values(template.value?.color_tokens || {})
    .map((t) => (t && typeof t === 'object' ? t.default || t.value : t))
  const seen = new Set()
  return [...fromPalette, ...fromTokens]
    .filter((v) => typeof v === 'string' && /^#?[0-9a-f]{3,8}$/i.test(v.trim()))
    .map((v) => (v.trim().startsWith('#') ? v.trim() : `#${v.trim()}`))
    .filter((v) => (seen.has(v.toLowerCase()) ? false : seen.add(v.toLowerCase())))
    .slice(0, 8)
})

// Serialize saves per block so a slow earlier response can't clobber a newer
// edit (last-write-wins races). Each block has its own promise chain.
const saveChains = new Map()
function queueSave(blockId, fn) {
  const prev = saveChains.get(blockId) || Promise.resolve()
  const next = prev.catch(() => {}).then(fn)
  saveChains.set(blockId, next)
  return next
}

// Commit any focused field (blur-save model) before navigating away so its
// edit isn't lost on page switch / unload.
function flushPendingEdits() {
  const el = document.activeElement
  if (el && typeof el.blur === 'function' && el !== document.body) el.blur()
}

function selectPage(id) {
  if (id === selectedPageId.value) { closeForm(); closeSeo(); return }
  flushPendingEdits()
  closeForm()
  closeSeo()
  selectedPageId.value = id
}

function formatDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

// Section card component refs, keyed by block.id. Vue's array template-refs
// don't guarantee order matches the v-for source, so we key by block.id to
// reliably resolve a card for the postMessage click-to-edit flow.
const cardRefByBlockId = new Map()
function setCardRef(blockId, el) {
  if (el) cardRefByBlockId.set(blockId, el)
  else cardRefByBlockId.delete(blockId)
}
const sectionList = ref(null)

// Back-compat shim: route all one-off notices + errors through the single
// save-state chip. Success notices auto-clear; errors stay until dismissed.
function flash(text, kind = 'ok') {
  if (kind === 'err') markError(text)
  else markSaved(text)
}

function blockRef(blockId) {
  for (const page of pages) {
    const block = page.blocks.find((b) => b.id === blockId)
    if (block) return block
  }
  return null
}

// Wire drag-and-drop reordering on the section list. When the user drops
// a card, we read the rendered DOM order, send it to the reorder endpoint,
// and persist locally on success. The renderer respects block.position
// over the template's static skeleton order, so the published site reflects
// the new sequence after the next build.
let sortable = null

function mountSortable() {
  if (!sectionList.value) return
  if (sortable) sortable.destroy()
  sortable = Sortable.create(sectionList.value, {
    animation: 150,
    handle: '.section-card-handle',
    onEnd: handleSortEnd
  })
}

watch(selectedPageId, () => nextTick(mountSortable), { immediate: false })
watch(sectionList, () => nextTick(mountSortable))

onBeforeUnmount(() => sortable?.destroy())

async function handleSortEnd() {
  const blockIds = Array.from(sectionList.value.querySelectorAll('[data-block-id]'))
    .map((el) => el.dataset.blockId)

  // Optimistic local reorder (without mutating reactive identity of blocks).
  const page = selectedPage.value
  if (!page) return
  const next = blockIds.map((id) => page.blocks.find((b) => b.id === id)).filter(Boolean)
  page.blocks.splice(0, page.blocks.length, ...next)

  try {
    await api.reorderSections(page.id, blockIds)
    bumpPreview()
    flash('Sections reordered')
  } catch (e) {
    flash(`Reorder failed: ${e.message}`, 'err')
  }
}

// ===== Undo / redo =====
// A command stack of { undo, redo } closures. Records content/item/visibility/
// section-move mutations (style overrides deep-merge server-side, so they're
// excluded; brand/chat/blog/page add-delete are config, not content editing).
// Applying history is guarded so it doesn't re-record.
const undoStack = ref([])
const redoStack = ref([])
const HISTORY_CAP = 50
let applyingHistory = false
const canUndo = computed(() => undoStack.value.length > 0)
const canRedo = computed(() => redoStack.value.length > 0)

function clone(v) { return JSON.parse(JSON.stringify(v ?? null)) }

function record(undoFn, redoFn) {
  if (applyingHistory) return
  redoStack.value = []
  undoStack.value.push({ undo: undoFn, redo: redoFn })
  if (undoStack.value.length > HISTORY_CAP) undoStack.value.shift()
}

// Structural section ops (add / duplicate / delete) change the SET of blocks,
// which the per-block content command stack can't represent. Leaving stale
// closures around lets a later undo/redo operate across the structural change
// and desync the local rail from the server (e.g. resurrecting a deleted
// section as a "ghost" card). Clear history at those boundaries.
function resetHistory() {
  undoStack.value = []
  redoStack.value = []
}

async function doUndo() {
  const entry = undoStack.value.pop()
  if (!entry) return
  applyingHistory = true
  try { await entry.undo(); redoStack.value.push(entry) } catch (e) { markError(`Couldn't undo: ${e.message}`) } finally { applyingHistory = false }
}
async function doRedo() {
  const entry = redoStack.value.pop()
  if (!entry) return
  applyingHistory = true
  try { await entry.redo(); undoStack.value.push(entry) } catch (e) { markError(`Couldn't redo: ${e.message}`) } finally { applyingHistory = false }
}

// ---- Low-level persistence (shared by handlers + undo/redo apply). Content is
// REPLACED server-side (assign_attributes), so a full prior snapshot restores
// the exact state. ----
async function persistContent(blockId, content) {
  markSaving()
  const data = await queueSave(blockId, () => api.updateSection(blockId, { content }))
  const block = blockRef(blockId)
  if (block) Object.assign(block, { content: data.block.content })
  bumpPreview()
  markSaved()
  return data
}

async function persistVisibility(blockId, visible) {
  const block = blockRef(blockId)
  if (block) block.visible = visible // optimistic
  markSaving()
  try {
    const data = await queueSave(blockId, () => api.updateSection(blockId, { visible }))
    if (block) block.visible = data.block.visible
    bumpPreview()
    markSaved()
  } catch (e) {
    if (block) block.visible = !visible // revert
    markError(`Visibility update failed: ${e.message}`)
    throw e
  }
}

async function applyOrder(page, orderIds) {
  const map = new Map(page.blocks.map((b) => [b.id, b]))
  const next = orderIds.map((id) => map.get(id)).filter(Boolean)
  page.blocks.splice(0, page.blocks.length, ...next)
  await api.reorderSections(page.id, orderIds)
  bumpPreview()
  markSaved()
}

// ---- Handlers ----
async function onSectionUpdate(blockId, content, patch) {
  livePatch(patch) // instant preview feedback (no reload)
  const block = blockRef(blockId)
  const before = clone(block?.content || {})
  const after = clone(content)
  try {
    await persistContent(blockId, content)
    record(() => persistContent(blockId, before), () => persistContent(blockId, after))
  } catch (e) {
    markError(`Save failed: ${e.message}`)
  }
}

async function onToggleVisibility(blockId, visible) {
  const prev = blockRef(blockId)?.visible
  try {
    await persistVisibility(blockId, visible)
    record(() => persistVisibility(blockId, prev), () => persistVisibility(blockId, visible))
  } catch (e) { /* persistVisibility reverted + reported */ }
}

// Keyboard-accessible section reorder (the drag handle is mouse-only).
async function onMoveSection(blockId, dir) {
  const page = selectedPage.value
  if (!page) return
  const idx = page.blocks.findIndex((b) => b.id === blockId)
  const target = idx + dir
  if (idx < 0 || target < 0 || target >= page.blocks.length) return

  const before = page.blocks.map((b) => b.id)
  const after = before.slice()
  const [moved] = after.splice(idx, 1)
  after.splice(target, 0, moved)
  markSaving()
  try {
    await applyOrder(page, after)
    markSaved('Section moved')
    record(() => applyOrder(page, before), () => applyOrder(page, after))
  } catch (e) {
    await applyOrder(page, before).catch(() => {}) // revert
    markError(`Move failed: ${e.message}`)
  }
}

async function onItemMutation(blockId, apiCall, okMsg) {
  const block = blockRef(blockId)
  const before = clone(block?.content || {})
  try {
    const data = await apiCall()
    if (block) Object.assign(block, { content: data.block.content })
    const after = clone(block?.content || {})
    bumpPreview()
    markSaved(okMsg)
    record(() => persistContent(blockId, before), () => persistContent(blockId, after))
  } catch (e) {
    markError(`${okMsg.split(' ')[0]} failed: ${e.message}`)
  }
}

function onAddItem(blockId) { return onItemMutation(blockId, () => api.addItem(blockId), 'Item added') }
function onRemoveItem(blockId, index) { return onItemMutation(blockId, () => api.removeItem(blockId, index), 'Item removed') }
function onReorderItems(blockId, orderedUids) { return onItemMutation(blockId, () => api.reorderItems(blockId, orderedUids), 'Items reordered') }

// Style/visibility overrides — server deep-merges the delta into the
// existing element_overrides jsonb, so we send only what changed.
async function onUpdateOverrides(blockId, delta) {
  const before = clone(blockRef(blockId)?.element_overrides || {})
  try {
    const data = await api.updateOverrides(blockId, delta)
    const after = clone(data.block.element_overrides || {})
    Object.assign(blockRef(blockId), {
      element_overrides: data.block.element_overrides,
      content_version: data.block.content_version
    })
    bumpPreview()
    record(() => persistOverrides(blockId, before), () => persistOverrides(blockId, after))
  } catch (e) {
    flash(`Style update failed: ${e.message}`, 'err')
  }
}

// Replace a block's element_overrides wholesale (undo/redo of style edits).
async function persistOverrides(blockId, overrides) {
  const data = await api.updateOverrides(blockId, overrides, true)
  Object.assign(blockRef(blockId), {
    element_overrides: data.block.element_overrides,
    content_version: data.block.content_version
  })
  bumpPreview()
}

// Version history drawer state — lazy-loaded per block.
const historyBlockId = ref(null)
const historyVersions = ref([])
const historyLoading = ref(false)
const historyError = ref(null)
const historyCloseBtn = ref(null)
let historyReturnFocus = null

// Friendly section name for the drawer header.
const historyBlockName = computed(() => {
  const block = historyBlockId.value && blockRef(historyBlockId.value)
  return block?.section?.name || block?.section?.key || 'this section'
})

async function openHistory(blockId) {
  historyReturnFocus = document.activeElement // restore focus on close (modal a11y)
  historyBlockId.value = blockId
  historyVersions.value = []
  historyError.value = null
  historyLoading.value = true
  nextTick(() => historyCloseBtn.value?.focus?.())
  try {
    const data = await api.listVersions(blockId)
    historyVersions.value = data.versions || []
  } catch (e) {
    historyError.value = e.message || 'Could not load history'
  } finally {
    historyLoading.value = false
  }
}

function closeHistory() {
  historyBlockId.value = null
  historyVersions.value = []
  historyError.value = null
  if (historyReturnFocus?.focus) historyReturnFocus.focus()
  historyReturnFocus = null
}

async function restoreVersion(versionId) {
  if (!historyBlockId.value) return
  const ok = await confirmAlert({
    title: 'Restore this version?',
    text: 'This replaces the section’s current content with this earlier version. Your current version is kept in history, so you can switch back.',
    icon: 'question',
    confirmButtonText: 'Restore'
  })
  if (!ok) return
  try {
    const data = await api.restoreVersion(historyBlockId.value, versionId)
    Object.assign(blockRef(historyBlockId.value), {
      content: data.block.content,
      element_overrides: data.block.element_overrides,
      content_version: data.block.content_version
    })
    bumpPreview()
    markSaved('Version restored')
    await openHistory(historyBlockId.value)
  } catch (e) {
    historyError.value = `Restore failed: ${e.message}`
  }
}

async function saveBusiness(newValues) {
  try {
    brandSaving.value = true
    const data = await api.updateBrand({ business: newValues })
    Object.assign(business, data.business)
    bumpPreview()
    flash('Business info saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function saveBrand(palette) {
  try {
    brandSaving.value = true
    const data = await api.updateBrand({ brand_palette: palette })
    Object.assign(brandPalette, data.brand_palette)
    bumpPreview()
    flash('Brand saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function saveBlog({ enabled }) {
  try {
    brandSaving.value = true
    const data = await api.updateBlog({ enabled })
    blogEnabled.value = !!data.blog_enabled
    bumpPreview()
    flash(blogEnabled.value ? 'Blog enabled' : 'Blog disabled')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function saveChat(payload) {
  try {
    brandSaving.value = true
    const data = await api.updateChat(payload)
    Object.assign(chatSettings, data.chat)
    bumpPreview()
    flash('Chat settings saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function saveBar(payload) {
  try {
    brandSaving.value = true
    const data = await api.updateBar(payload)
    Object.assign(barSettings, data.bar)
    bumpPreview()
    flash('Bar settings saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function savePopup(payload) {
  try {
    brandSaving.value = true
    const data = await api.updatePopup(payload)
    Object.assign(popupSettings, data.popup)
    bumpPreview()
    flash('Popup settings saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function saveHeader(content) {
  try {
    brandSaving.value = true
    const data = await api.updateHeader(content)
    if (data.header) Object.assign(headerSettings, data.header)
    bumpPreview()
    flash('Header saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function saveFooter(content) {
  try {
    brandSaving.value = true
    const data = await api.updateFooter(content)
    if (data.footer) Object.assign(footerSettings, data.footer)
    bumpPreview()
    flash('Footer saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

async function saveSeoSettings(payload) {
  try {
    brandSaving.value = true
    const data = await api.updateSeoSettings(payload)
    if (data.seo_settings) {
      Object.assign(seoSettings, data.seo_settings)
      // Keep the per-page editor's fallbacks (default image, description) in sync.
      if (seoDefaults) {
        seoDefaults.description = data.seo_settings.description
        seoDefaults.default_og_image_url = data.seo_settings.default_og_image_url
      }
    }
    flash('SEO settings saved')
  } catch (e) {
    flash(`Save failed: ${e.message}`, 'err')
  } finally {
    brandSaving.value = false
  }
}

// Page fan-out handlers. The Sidebar emits add-page with a payload that
// includes site_template_page_id + a service_id and/or service_area_id;
// we POST to the new endpoint and append the new page to local state so
// the rail updates without a full reload.
async function onAddPage({ site_template_page_id, service_id, service_area_id, onComplete }) {
  try {
    const data = await api.addPage({ site_template_page_id, service_id, service_area_id })
    if (data.page) {
      // Server now returns the page WITH its blocks, so it's editable
      // immediately. Add to the rail and select it.
      pages.push({ ...data.page, blocks: data.page.blocks || []})
      selectedPageId.value = data.page.id
      flash(`Added ${data.page.title}`)
      bumpPreview()
      onComplete?.({ success: true })
    }
  } catch (e) {
    flash(`Could not add page: ${e.message}`, 'err')
    onComplete?.({ success: false, error: e.message })
  }
}

// Image generation is async (Sidekiq). The WebsiteGenerationChannel
// subscription (see onMounted) drives the button + refreshes the preview as
// each image lands, instead of a fixed timer that re-enabled too early and
// never showed the results.
let generationQuietTimer = null
let generationHardCap = null
function endGenerating() {
  generatingImages.value = false
  clearTimeout(generationQuietTimer)
  clearTimeout(generationHardCap)
  // Sync the editor's image thumbnails with the freshly generated content
  // (the preview already reflects it; this updates the field models too).
  refreshActivePageContent()
}

// Pull the current page's serialized blocks and merge content/overrides into
// local state in place — keyed by block id so open section cards stay open.
async function refreshActivePageContent() {
  const page = selectedPage.value
  if (!page) return
  try {
    const data = await api.pageContent(page.id)
    if (!data.page?.blocks) return
    data.page.blocks.forEach((fresh) => {
      const block = page.blocks.find((b) => b.id === fresh.id)
      if (block) block.content = fresh.content
    })
  } catch (e) { /* non-fatal */ }
}
function noteGenerationActivity() {
  if (!generatingImages.value) return
  bumpPreview() // images appear as they're generated
  clearTimeout(generationQuietTimer)
  generationQuietTimer = setTimeout(endGenerating, 6000) // idle = done
}

// Per-image generate: an explicit single-slot action (1 credit), so no modal —
// it kicks off the async job, the preview live-updates, and the thumbnail syncs
// when generation settles (refreshActivePageContent).
async function onGenerateImage(blockId, { field, index }) {
  try {
    generatingImages.value = true
    clearTimeout(generationHardCap)
    generationHardCap = setTimeout(endGenerating, 120000)
    await api.generateImage(blockId, field, index)
    markUnpublished()
    markSaved('Generating image — it appears in the preview shortly')
  } catch (e) {
    endGenerating()
    markError(`Generation failed: ${e.message}`)
  }
}

async function onGeneratePageImages() {
  if (!selectedPage.value) return
  const ok = await confirmAlert({
    title: 'Generate images for this page?',
    text: 'This uses AI image credits to create a fresh image for every image slot on this page, replacing the current ones.',
    icon: 'question',
    confirmButtonText: 'Generate images'
  })
  if (!ok) return
  try {
    generatingImages.value = true
    clearTimeout(generationHardCap)
    generationHardCap = setTimeout(endGenerating, 120000) // safety stop
    await api.generatePageImages(selectedPage.value.id)
    markSaved('Generating images — they appear as they finish')
  } catch (e) {
    endGenerating()
    markError(`Generation failed: ${e.message}`)
  }
}

// ---- Form editor save handlers ----
// FormEditor emits the full desired field list / settings; the controller
// persists, rebuilds the edit-mode bundle (RenderForm re-renders), and returns
// the re-serialized form which we merge back so ids assigned to new fields
// flow into the panel.
function mergeForm(updated) {
  const idx = forms.findIndex((f) => f.id === updated.id)
  if (idx >= 0) Object.assign(forms[idx], updated)
}

async function onSaveFormFields(fields) {
  const form = selectedForm.value
  if (!form) return
  markSaving()
  try {
    const data = await api.updateFormFields(form.id, fields)
    if (data.form) mergeForm(data.form)
    bumpPreview()
    markSaved('Form fields saved')
  } catch (e) {
    markError(`Save failed: ${e.message}`)
  }
}

async function onSaveFormSettings(settings) {
  const form = selectedForm.value
  if (!form) return
  markSaving()
  try {
    const data = await api.updateFormSettings(form.id, settings)
    if (data.form) mergeForm(data.form)
    bumpPreview()
    markSaved('Form settings saved')
  } catch (e) {
    markError(`Save failed: ${e.message}`)
  }
}

// SeoEditor emits the full SEO field set (debounced). Persist via the page SEO
// endpoint and merge the normalized values back so the panel reflects them.
async function onSaveSeo(seo) {
  const page = selectedPage.value
  if (!page) return
  markSaving()
  try {
    const data = await api.updatePageSeo(page.id, seo)
    if (data.seo) page.seo = { ...page.seo, ...data.seo }
    markSaved('SEO saved')
  } catch (e) {
    markError(`Save failed: ${e.message}`)
  }
}

// ============================================
// Click-to-edit: postMessage bridge with the iframe overlay
// ============================================
//
// EditOverlay (built into every edit-mode page) sends:
//   { source: 'mly-edit-overlay', kind: 'select-field', section, field, item_uid }
//   { source: 'mly-edit-overlay', kind: 'upload-image', section, field, item_uid, file: { name, type, data_url } }
//   { source: 'mly-edit-overlay', kind: 'ready' }
//
// We answer by opening the right page + section card and focusing the
// matching field input. Image drops upload via the existing TemplateImages
// endpoint and patch the block's content.<field>.

// Resolve a section key to its block. Section keys (hero, cta_band, …) are
// reused across many pages, so search order matters: prefer the page the
// click actually came from (ctx.page_id from the overlay), then the page
// already open, then any page. Without this, clicking a hero on the Contact
// page resolves to the home page's hero and yanks the editor back to Home.
function findBlockBySectionKey(sectionKey, preferredPageId = null) {
  const ordered = []
  const pushUnique = (page) => { if (page && !ordered.includes(page)) ordered.push(page) }
  pushUnique(pages.find((p) => p.id === preferredPageId))
  pushUnique(selectedPage.value)
  pages.forEach(pushUnique)

  for (const page of ordered) {
    for (const block of page.blocks || []) {
      if (block.section?.key === sectionKey) return { page, block }
    }
  }
  return null
}

async function openFieldInEditor(sectionKey, fieldKey, opts = {}) {
  const match = findBlockBySectionKey(sectionKey, opts.pageId)
  if (!match) return false

  if (selectedPageId.value !== match.page.id) {
    selectedPageId.value = match.page.id
    await nextTick()
  }

  await nextTick()
  // Expand the matched block's card (keyed by block.id, not render index).
  cardRefByBlockId.get(match.block.id)?.expand?.()

  await nextTick()
  const cardEl = sectionList.value?.querySelector(`[data-block-id="${cssEscape(match.block.id)}"]`)
  if (!cardEl) return false

  let fieldEl = cardEl.querySelector(`[data-field-key="${cssEscape(fieldKey)}"]`)
  if (opts.itemUid) {
    const itemEl = cardEl.querySelector(`[data-item-uid="${cssEscape(opts.itemUid)}"]`)
    if (itemEl) fieldEl = itemEl.querySelector(`[data-field-key="${cssEscape(fieldKey)}"]`) || itemEl
  }

  (fieldEl || cardEl).scrollIntoView({ behavior: 'smooth', block: 'center' })
  const input = fieldEl?.querySelector('input, textarea, [contenteditable="true"]')
  input?.focus?.()
  return true
}

function cssEscape(value) {
  return String(value || '').replace(/"/g, '\\"')
}

async function uploadOverlayImage(msg) {
  try {
    const blob = await (await fetch(msg.file.data_url)).blob()
    const file = new File([blob], msg.file.name || 'upload', { type: msg.file.type || blob.type })
    const data = await api.uploadImage(file, msg.field)
    const url = data?.url
    if (!url) throw new Error('no url in upload response')

    const match = findBlockBySectionKey(msg.section, msg.ctx?.page_id)
    if (!match) return
    const isRepeatable = !!match.block.section?.repeatable
    let content
    if (isRepeatable && msg.item_uid) {
      const items = (match.block.content?.items || []).map((it) => (
        (it._uid || '') === msg.item_uid ? { ...it, [msg.field]: url } : it
      ))
      content = { ...(match.block.content || {}), items }
    } else {
      content = { ...(match.block.content || {}), [msg.field]: url }
    }
    await onSectionUpdate(match.block.id, content)
    flash('Image uploaded')
  } catch (e) {
    flash(`Upload failed: ${e.message}`, 'err')
  }
}

function onOverlayMessage(event) {
  const msg = event.data || {}
  if (msg.source !== 'mly-edit-overlay') return
  switch (msg.kind) {
    case 'select-field':
      if (!msg.section || !msg.field) return
      // Leave the SEO panel so the section's content fields are visible.
      closeSeo()
      openFieldInEditor(msg.section, msg.field, { itemUid: msg.item_uid, pageId: msg.ctx?.page_id })
      break
    case 'select-form':
      if (msg.form_id) { closeSeo(); openForm(msg.form_id) }
      break
    case 'upload-image':
      uploadOverlayImage(msg)
      break
    case 'ready':
      // Overlay handshake — nothing to do for now.
      break
  }
}

// Esc closes the history modal; ⌘Z / ⌘⇧Z (Ctrl on Win) undo/redo — but only
// when not typing in a field, so native text-field undo still works.
function onKeydown(e) {
  // Esc closes whatever overlay/panel is open, most-modal first.
  if (e.key === 'Escape') {
    if (historyBlockId.value) { e.stopPropagation(); closeHistory(); return }
    if (addSectionOpen.value) { e.stopPropagation(); addSectionOpen.value = false; return }
    if (seoOpen.value) { e.stopPropagation(); closeSeo(); return }
    if (selectedForm.value) { e.stopPropagation(); closeForm(); return }
  }
  if (!(e.metaKey || e.ctrlKey)) return
  const el = document.activeElement
  const tag = (el?.tagName || '').toLowerCase()
  if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) return
  const k = e.key.toLowerCase()
  if (k === 'z' && !e.shiftKey) { e.preventDefault(); doUndo() } else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); doRedo() }
}

// Warn before leaving while a save is in flight (avoid losing the last edit).
function onBeforeUnload(e) {
  if (saveState.value === 'saving') { e.preventDefault(); e.returnValue = '' }
}

// Live image-generation updates: refresh the preview as images land + drive
// the Generate button state from the channel rather than a fixed timer.
let generationSub = null

onMounted(() => {
  // Narrow screens can't fit all three columns — start with the editor only.
  if (typeof window !== 'undefined' && window.innerWidth < 1280) {
    sidebarOpen.value = false
    previewOpen.value = false
  }
  window.addEventListener('message', onOverlayMessage)
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('beforeunload', onBeforeUnload)

  generationSub = consumer.subscriptions.create(
    { channel: 'WebsiteGenerationChannel', website_id: props.websiteId },
    {
      received: (data) => {
        if (data?.event === 'template_images_failed') {
          endGenerating()
          markError('Image generation failed')
          return
        }
        noteGenerationActivity()
      }
    }
  )
})
onBeforeUnmount(() => {
  window.removeEventListener('message', onOverlayMessage)
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('beforeunload', onBeforeUnload)
  generationSub?.unsubscribe()
})

async function onDeletePage(pageId) {
  const page = pages.find((p) => p.id === pageId)
  if (!page) return
  if (!confirm(`Delete "${page.title}"?`)) return
  try {
    await api.deletePage(pageId)
    const idx = pages.findIndex((p) => p.id === pageId)
    if (idx >= 0) pages.splice(idx, 1)
    if (selectedPageId.value === pageId) selectedPageId.value = pages[0]?.id || null
    flash('Page deleted')
    bumpPreview()
  } catch (e) {
    flash(`Could not delete: ${e.message}`, 'err')
  }
}
</script>
